import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import './Contact.css';

const faqs = [
  {
    q: 'Does MyAlpha require the internet?',
    a: 'Day-to-day inference can run fully local. Updates and optional support channels are under your control.',
  },
  {
    q: 'What models do you use?',
    a: 'Enterprise open-weight and licensed model families you approve — selected and tuned for your industry pack.',
  },
  {
    q: 'How is this different from ChatGPT Enterprise?',
    a: 'MyAlpha is not a shared cloud brain. Weights, indexes, and logs stay on hardware you control.',
  },
  {
    q: 'Can we set our own content policy?',
    a: 'Yes. That is a core design goal — your acceptable-use rules, enforced by you.',
  },
  {
    q: 'Who installs MyAlpha?',
    a: 'We deploy with your IT/security team; MyAlpha Rack arrives as a sized appliance, MyAlpha on NVIDIA is loaded onto qualified hardware.',
  },
];

const industries = [
  'Automotive',
  'Heavy Equipment',
  'Medical',
  'Supply Chain',
  'Legal',
  'Manufacturing',
  'Energy & Utilities',
  'Financial Services',
  'Other / Custom',
];

const SOURCE_META = {
  'home-briefing': {
    button: 'Request briefing (Home)',
    label: 'Home — Request a MyAlpha briefing',
  },
  'home-cta': {
    button: 'Talk to sales (Home)',
    label: 'Home — Ready to keep AI inside the fence',
  },
  nav: {
    button: 'Talk to sales (Nav)',
    label: 'Header — Talk to sales',
  },
  footer: {
    button: 'Talk to sales (Footer)',
    label: 'Footer — Talk to sales',
  },
  myalpha: {
    button: 'Schedule sizing call',
    label: 'MyAlpha — Schedule a sizing call',
  },
  industry: {
    button: 'Discuss industry pack',
    label: 'Industries — Discuss this pack',
  },
  'industry-custom': {
    button: 'Custom industry pack',
    label: 'Industries — Custom pack',
  },
};

const DEFAULT_SOURCE = {
  button: 'Request briefing',
  label: 'Direct / Contact page',
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
  },
};

const emptyForm = {
  name: '',
  email: '',
  company: '',
  industry: '',
  deployment: '',
  message: '',
};

function resolveSource(source, pack) {
  const meta = SOURCE_META[source] || DEFAULT_SOURCE;

  if (source === 'industry' && pack) {
    return {
      button: `Discuss ${pack} pack`,
      label: `Industries — Discuss ${pack} pack`,
    };
  }

  return meta;
}

export const Contact = () => {
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || '';
  const pack = searchParams.get('pack') || '';
  const sourceMeta = useMemo(() => resolveSource(source, pack), [source, pack]);

  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [activeFaq, setActiveFaq] = useState(null);

  useEffect(() => {
    if (pack && industries.includes(pack)) {
      setFormData((prev) => ({ ...prev, industry: pack }));
    }
  }, [pack]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: source || 'direct',
          sourceLabel: sourceMeta.label,
          pack: pack || '',
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }

      setSubmitted(true);
      setFormData(emptyForm);
    } catch (err) {
      setSubmitError(err.message || 'Failed to send message. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      className="contact-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <section className="contact-hero">
        <div className="container">
          <motion.div className="contact-hero-content" variants={itemVariants}>
            <h1>Tell us where MyAlpha should live.</h1>
            <p className="lead">
              Typical next step is a 30-minute briefing: data boundaries, concurrent
              users, and whether a MyAlpha Rack or your NVIDIA box is the better fit.
            </p>
            <div className="contact-direct">
              <a href="tel:+16084213923">(608) 421-3923</a>
              <a href="mailto:sales@runduckrun.com">sales@runduckrun.com</a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            <motion.div
              className="contact-form-container"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Request a MyAlpha briefing</h2>
              {source && SOURCE_META[source] && (
                <p className="form-reached-via">Reached via: {sourceMeta.label}</p>
              )}
              {submitted ? (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <h3>Request received</h3>
                  <p>
                    Thank you. Our team will follow up to schedule your MyAlpha briefing.
                  </p>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSubmitted(false)}
                  >
                    Send another request
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Work Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="jane@company.com"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="company">Company *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Acme Manufacturing"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="industry">Industry *</label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select industry</option>
                        {industries.map((ind) => (
                          <option key={ind} value={ind}>
                            {ind}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="deployment">Deployment interest *</label>
                      <select
                        id="deployment"
                        name="deployment"
                        value={formData.deployment}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select option</option>
                        <option value="MyAlpha Rack">MyAlpha Rack</option>
                        <option value="MyAlpha on NVIDIA">MyAlpha on NVIDIA</option>
                        <option value="Not sure">Not sure</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Users, data boundaries, timeline, or hardware you already own…"
                      rows={5}
                    />
                  </div>
                  {submitError && (
                    <p className="form-error" role="alert">
                      {submitError}
                    </p>
                  )}
                  <motion.button
                    type="submit"
                    className="btn btn-primary btn-submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        {sourceMeta.button}
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                          <path d="M14 2L7 9M14 2L9.5 14L7 9M14 2L2 6.5L7 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </motion.div>

            <motion.div
              className="contact-faq"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Frequently asked questions</h2>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={faq.q}
                    className={`faq-item ${activeFaq === index ? 'active' : ''}`}
                    initial={false}
                  >
                    <button
                      className="faq-question"
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                    >
                      <span>{faq.q}</span>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{ transform: activeFaq === index ? 'rotate(180deg)' : 'none' }}
                        aria-hidden="true"
                      >
                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <motion.div
                      className="faq-answer"
                      initial={false}
                      animate={{
                        height: activeFaq === index ? 'auto' : 0,
                        opacity: activeFaq === index ? 1 : 0,
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <p>{faq.a}</p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Contact;
